<template>
    <div>
        <integration-data-filter-form
            v-if="mode === 'add' || mode === 'edit'"
            :integrationId="resolvedIntegrationId"
            :is_disabled="is_disabled"
            :isSaving="isSaving"
            @back="mode = 'list'"
            :title="mode === 'edit' ? 'Edit Data Filter' : 'Add Data Filter'"
            :initialDraft="mode === 'edit' ? editDraft : null"
            @save="mode === 'edit' ? onUpdate(editFilterId, $event) : onCreate($event)"
        />

        <div v-else>
            <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-5"></v-progress-linear>

            <v-card-title>
                <v-spacer></v-spacer>

                <v-btn small class="mr-2" :disabled="is_disabled" @click="testDialog = true">
                    Test Data Filter
                </v-btn>

                <v-btn small class="mr-2" @click="shareFilters" :disabled="is_disabled || isSharing">
                    <span v-if="isSharing">
                        <v-progress-circular
                            indeterminate
                            size="12"
                            width="2"
                            color="primary darken-3">
                        </v-progress-circular>
                        Generating, Please wait...
                    </span>
                    <span v-else>{{ shareBtnText }}</span>
                </v-btn>

                <v-btn small color="primary" :disabled="is_disabled" @click="mode = 'add'">
                    Add Data Filter
                </v-btn>
            </v-card-title>

            <div v-if="selectedIds.length > 0" class="m-3">
                <v-btn
                    x-small
                    class="white--text"
                    :disabled="is_disabled"
                    color="#FF7033" @click="onDeleteSelected"
                >
                    Delete Selected
                </v-btn>
            </div>

            <table class="table table-hover">
                <thead>
                    <tr>
                        <th v-if="items.length > 1" width="40">
                            <v-checkbox
                                v-model="selectAll"
                                @change="toggleAll"
                                class="ma-0 pa-0"
                                hide-details
                                dense
                            />
                        </th>
                        <th>Label</th>
                        <th width="120">Filter Type</th>
                        <th width="100">Hook</th>
                        <th>Conditions</th>
                        <th width="200">Status</th>
                        <th width="170">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!isLoading && items.length === 0">
                        <td :colspan="items.length > 1 ? 7 : 6" class="text-center py-4">No Data filters yet.</td>
                    </tr>

                    <tr v-for="item in items" :key="item.id">
                        <td v-if="items.length > 1" width="40">
                            <v-checkbox
                                v-model="selectedIds"
                                :value="item.id"
                                class="ma-0 pa-0"
                                hide-details
                                dense
                            />
                        </td>
                        <td>{{ item.label }}</td>
                        <td>
                            <v-chip x-small :color="item.filter_type === 'include' ? 'success' : 'warning'" text-color="white">
                                {{ (item.filter_type || 'include').toUpperCase() }}
                            </v-chip>
                        </td>
                        <td>
                            <v-chip x-small color="primary" text-color="white">
                                {{ (item.hook_type || 'pre').toUpperCase() }}
                            </v-chip>
                        </td>
                        <td>
                            <div class="wrap-cell">
                                <small>{{ item.conditions }}</small>
                            </div>
                        </td>
                        <td>
                            <v-switch
                                @change="onToggleStatus(item)"
                                :disabled="is_disabled || !!statusToggling[item.id]"
                                :loading="!!statusToggling[item.id]"
                                v-model="item.status"
                                :label="item.status ? 'Enabled' : 'Disabled'"
                                hide-details
                                dense
                                class="mt-0 d-inline-block"
                            />
                        </td>
                        <td>
                            <v-btn x-small class="mr-1" :disabled="is_disabled" @click="onEdit(item)">Edit</v-btn>
                            <v-btn x-small class="white--text" :disabled="is_disabled" color="#FF7033" @click="onDelete(item)">Delete</v-btn>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Delete confirmation dialog -->
            <v-dialog v-model="deleteDialog" max-width="420px" persistent>
                <v-card>
                    <v-card-title class="headline">Delete Filter</v-card-title>
                    <v-card-text>
                        Are you sure you want to delete
                        <strong>{{ deleteItemLabel }}</strong>?
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn text color="default" :disabled="isDeleting" @click="closeDeleteDialog">Cancel</v-btn>
                        <v-btn class="white--text" color="#FF7033" :loading="isDeleting" :disabled="isDeleting" @click="confirmDelete">
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Test Data Filter dialog -->
            <test-data-dialog
                v-model="testDialog"
                title="Test Data Filter"
                button-label="Data Filter"
                :summary-text="testSummaryText"
                @test="handleTestDataFilter">
                <template #result="{ response, result }">
                    <div class="d-flex align-center mb-3">
                        <v-spacer />
                        <v-switch
                            v-model="showRawTestJson"
                            label="View raw JSON"
                            hide-details
                            dense
                            class="mt-0"
                        />
                    </div>

                    <div v-if="!showRawTestJson">
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>Label</th>
                                    <th width="120">Filter Type</th>
                                    <th width="120">Hook Type</th>
                                    <th width="100">Status</th>
                                    <th width="140">Matched</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="r in (response.results || [])" :key="r.id">
                                    <td>{{ r.label || `Filter #${r.id}` }}</td>
                                    <td>{{ (r.filter_type || '').toUpperCase() }}</td>
                                    <td>{{ (r.hook_type || '').toUpperCase() }}</td>
                                    <td>
                                        <v-chip
                                            x-small
                                            :color="r.status ? '#F2F3FF' : '#FFFFFF'"
                                            :text-color="r.status ? '#00073C' : '#00073C'"
                                            :style="'border: 1px solid ' + (r.status ? '#004AAD' : '#DEDEDE')"
                                        >
                                            {{ r.status ? 'ENABLED' : 'DISABLED' }}
                                        </v-chip>
                                    </td>
                                    <td>
                                        <v-chip
                                            x-small
                                            :color="r.matched ? '#F2F3FF' : '#FFFFFF'"
                                            :text-color="r.matched ? '#00073C' : '#00073C'"
                                            :style="'border: 1px solid ' + (r.matched ? '#004AAD' : '#DEDEDE')"
                                        >
                                            {{ r.matched ? 'YES' : 'NO' }}
                                        </v-chip>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <v-card v-else outlined class="result-card">
                        <v-card-text class="pa-3">
                            <pre class="result-code">{{ result }}</pre>
                        </v-card-text>
                    </v-card>
                </template>
            </test-data-dialog>
        </div>
    </div>
</template>

<script>
import IntegrationDataFilterForm from './IntegrationDataFilterForm.vue'
import TestDataDialog from './TestDataDialog.vue'
import { EventBus } from '@/js/event-bus.js'

// Static UI first. This component is structured to be fetch-ready against:
//   GET /api/integration-data-filters/{integrationId}
// Later we can wire create/update/delete/sort via the existing endpoints.

export default {
    components: {
        IntegrationDataFilterForm,
        TestDataDialog,
    },

    props: {
        integrationId: { type: [Number, String], default: null },
        is_disabled: { type: Boolean, default: false },
    },

    computed: {
        // Safety: even if parent passes undefined initially, we can fall back to the route param.
        resolvedIntegrationId() {
            return this.integrationId || this.$route.params.id
        },

        deleteItemLabel() {
            return this.deleteItem?.label || `Filter #${this.deleteItem?.id || ''}`
        },

        testSummaryText() {
            const results = this.testResponse?.results || []
            if (!this.testResponse) return 'Results'

            const matchedCount = results.filter(r => !!r.matched).length
            const total = results.length
            return `${matchedCount} matched out of ${total} total filters`
        },
    },

    data() {
        return {
            mode: 'list',
            editFilterId: null,
            editDraft: null,
            isLoading: false,
            isSaving: false,
            statusToggling: {},

            shareBtnText: 'Share Filter',
            originalShareBtnText: 'Share Filter',
            isSharing: false,

            deleteDialog: false,
            deleteItem: null,
            isDeleting: false,
            items: [],

            selectAll: false,
            selectedIds: [],
            updatingSelectAll: false,

            // Test Data Filter
            testDialog: false,
            testResponse: null,
            showRawTestJson: false,
        }
    },

    mounted() {
        this.fetchDataFilters();
    },

    watch: {
        resolvedIntegrationId() {
            if (this.mode === 'list') {
                this.fetchDataFilters();
            }
        },

        selectedIds: {
            deep: true,
            handler() {
                if (this.items.length <= 1) {
                    this.updatingSelectAll = true
                    this.selectAll = false
                    this.$nextTick(() => { this.updatingSelectAll = false })
                    return
                }

                this.updatingSelectAll = true
                this.selectAll = this.selectedIds.length === this.items.length
                this.$nextTick(() => { this.updatingSelectAll = false })
            }
        },
    },

    methods: {
        handleTestDataFilter({ payload, onSuccess, onError }) {
            const integrationId = this.getIntegrationIdNumber()
            if (!integrationId) {
                EventBus.$emit('show-error', 'Missing integration id. Please refresh and try again.')
                onError(new Error('Missing integration id'))
                return
            }

            this.$http
                .post(`/api/data-filters/test/${integrationId}`, { payload })
                .then(({ data }) => {
                    this.testResponse = data?.data || null
                    const result = JSON.stringify(data?.data || data, null, 2)
                    onSuccess(this.testResponse, result)
                })
                .catch((err) => {
                    EventBus.$emit('show-error', err)
                    onError(err)
                })
        },

        normalizeOperator(op) {
            // Keep summary consistent even if older records store different casing/aliases.
            const map = {
                'equals': '==',
                'not equals': '!=',
                'in list': 'In List',
                'not in list': 'Not In List',
                'contains': 'Contains',
                'not contains': 'Not Contains',
                'is empty': 'Is Empty',
                'is true': 'Is True',
                'is false': 'Is False',
            }

            const key = typeof op === 'string' ? op.trim().toLowerCase() : op
            return map[key] || op
        },

        normalizeStatus(value) {
            // API may return boolean, int(0/1), or string("0"/"1").
            // Using !!value is WRONG for string "0" (truthy), so normalize explicitly.
            return value === true || value === 1 || value === '1'
        },

        toggleAll() {
            if (this.updatingSelectAll) return

            if (this.selectAll) {
                this.selectedIds = this.items.map(item => item.id)
            } else {
                this.selectedIds = []
            }
        },

        onDeleteSelected() {
            if (this.selectedIds.length === 0) return

            this.deleteItem = {
                id: this.selectedIds.join('-'),
                label: `${this.selectedIds.length} Selected Data Filters`,
            }
            this.deleteDialog = true
        },

        async shareFilters() {
            const integrationId = this.getIntegrationIdNumber()
            if (!integrationId) {
                EventBus.$emit('show-error', 'Missing integration id. Please refresh and try again.')
                return
            }

            this.isSharing = true

            try {
                const { data } = await this.$http.get(`/api/data-filters/${integrationId}`)

                const filters = Array.isArray(data?.data) ? data.data : []

                if (!(navigator.clipboard && navigator.clipboard.writeText)) {
                    EventBus.$emit('show-error', 'Clipboard API not supported in this environment.')
                    return
                }

                let copiedTxt = 'Data filters\n'

                filters.forEach((f) => {
                    copiedTxt += `\n--------------------------------------------------------------\n`
                    copiedTxt += `${f.label || `Filter #${f.id}`}\n`
                    copiedTxt += `Status: ${this.normalizeStatus(f.status) ? 'Enabled' : 'Disabled'}\n`
                    copiedTxt += `Filter Type: ${f.filter_type || 'include'}\n`
                    copiedTxt += `Hook Type: ${f.hook_type || 'pre'}\n`

                    const conditionsSummary = this.formatConditions(f.rule_conditions || [])
                    copiedTxt += `Conditions: ${conditionsSummary}\n`
                })

                await navigator.clipboard.writeText(copiedTxt)

                this.shareBtnText = 'Copied to clipboard'
                setTimeout(() => {
                    this.shareBtnText = this.originalShareBtnText
                }, 2000)

            } catch (err) {
                EventBus.$emit('show-error', 'Failed to generate share text.')
            } finally {
                this.isSharing = false
            }
        },

        async onToggleStatus(item) {
            const id = item?.id
            if (!id) return

            // optimistic UI: v-model already flipped it. We'll revert on error.
            const nextStatus = !!item.status
            const prevStatus = !nextStatus

            this.$set(this.statusToggling, id, true)

            try {
                await this.$http.post(`/api/data-filters/${id}`, {
                    status: nextStatus ? 1 : 0,
                })
                EventBus.$emit('show-success', `Filter ${nextStatus ? 'enabled' : 'disabled'}.`)
            } catch (err) {
                // revert
                item.status = prevStatus
                EventBus.$emit('show-error', 'Failed to update filter status.')
            } finally {
                this.$delete(this.statusToggling, id)
            }
        },

        getIntegrationIdNumber() {
            const id = parseInt(this.resolvedIntegrationId, 10)
            return Number.isFinite(id) ? id : null
        },

        async fetchDataFilters() {
            const integrationId = this.getIntegrationIdNumber()
            if (!integrationId) {
                // Avoid calling API with invalid id (would generate /undefined and/or null payloads)
                this.items = []
                this.selectAll = false
                this.selectedIds = []
                return
            }

            this.isLoading = true

            try {
                // Backend endpoint:
                //   GET /api/data-filters/{integration_id}
                const { data } = await this.$http.get(`/api/data-filters/${integrationId}`)

                const filters = Array.isArray(data?.data) ? data.data : []

                this.items = filters.map((f) => ({
                    id: f.id,
                    label: f.label || `Filter #${f.id}`,
                    filter_type: f.filter_type || 'include',
                    hook_type: f.hook_type || 'pre',
                    status: this.normalizeStatus(f.status),
                    rule_conditions: f.rule_conditions || [],
                    conditions: this.formatConditions(f.rule_conditions || []),
                    raw: f,
                }))

                // Reset selection to avoid stale ids after refresh
                this.selectAll = false
                this.selectedIds = []
            } catch (err) {
                EventBus.$emit('show-error', 'Failed to fetch data filters.')
                this.items = []
                this.selectAll = false
                this.selectedIds = []
            } finally {
                this.isLoading = false
            }
        },

        formatConditions(ruleConditions) {
            // Keep it simple for now: show a readable summary.
            // ruleConditions = [ group1, group2, ... ]
            // each group = [ condition1, condition2, ... ] (OR)
            if (!Array.isArray(ruleConditions) || ruleConditions.length === 0) {
                return 'No conditions'
            }

            const groupStrings = ruleConditions.map((group) => {
                if (!Array.isArray(group) || group.length === 0) return '(empty)'

                const condStrings = group.map((c) => {
                    const field = c.metaFieldName ? `${c.fieldName}.${c.metaFieldName}` : c.fieldName
                    const op = this.normalizeOperator(c.operator || '')
                    const val = c.value
                    return `${field} ${op} ${val}`.trim()
                })

                return condStrings.join(' OR ')
            })

            // If it gets too long, truncate.
            const text = 'IF ' + groupStrings.join(' AND ')
            return text.length > 120 ? text.slice(0, 117) + '...' : text
        },

        onEdit(item) {
            const integrationId = this.getIntegrationIdNumber()
            if (!integrationId) {
                EventBus.$emit('show-error', 'Missing integration id. Please refresh and try again.')
                return
            }

            this.editFilterId = item.id
            this.editDraft = this.filterToDraft(item)
            this.mode = 'edit'
        },

        onDelete(item) {
            this.deleteItem = item
            this.deleteDialog = true
        },

        closeDeleteDialog() {
            this.deleteDialog = false
            this.deleteItem = null
        },

        async confirmDelete() {
            const id = this.deleteItem?.id
            if (!id) {
                EventBus.$emit('show-error', 'Missing filter id. Please refresh and try again.')
                return
            }

            this.isDeleting = true

            try {
                await this.$http.delete(`/api/data-filters/${id}`)
                EventBus.$emit('show-success', 'Data filter deleted.')
                this.closeDeleteDialog()

                this.selectAll = false
                this.selectedIds = []

                await this.fetchDataFilters()
            } catch (err) {
                EventBus.$emit('show-error', 'Failed to delete data filter.')
            } finally {
                this.isDeleting = false
            }
        },

        filterToDraft(item) {
            const raw = item?.raw || item || {}
            const rule = raw.rule_conditions || item.rule_conditions || []

            return {
                label: raw.label || item.label || '',
                filterType: raw.filter_type || raw.filterType || 'include',
                hookType: raw.hook_type || raw.hookType || 'pre',
                status: raw.status !== undefined ? this.normalizeStatus(raw.status) : true,
                conditionGroups: (Array.isArray(rule) ? rule : []).map((group) => ({
                    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    rows: (Array.isArray(group) ? group : []).map((c) => ({
                        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        fieldName: c.metaFieldName ? `${c.fieldName}.${c.metaFieldName}` : (c.fieldName || ''),
                        operator: this.normalizeOperator(c.operator || '=='),
                        value: c.value ?? '',
                    })),
                })),
            }
        },

        normalizeDraftToPayload(draft) {
            // Convert UI draft -> backend payload shape.
            // fieldName supports dot notation (we'll store it in fieldName only).
            return {
                label: draft.label,
                filter_type: draft.filterType,
                hook_type: draft.hookType,
                status: draft.status ? 1 : 0,
                rule_conditions: (draft.conditionGroups || []).map((group) =>
                    (group.rows || []).map((row) => ({
                        fieldName: row.fieldName,
                        operator: row.operator,
                        value: row.value,
                    }))
                ),
            }
        },

        async onCreate(draft) {
            this.isSaving = true

            try {
                const integrationId = this.getIntegrationIdNumber()
                if (!integrationId) {
                    EventBus.$emit('show-error', 'Missing integration id. Please refresh and try again.')
                    return
                }

                const payload = {
                    integration_id: integrationId,
                    ...this.normalizeDraftToPayload(draft),
                }

                await this.$http.post('/api/data-filters', payload)

                EventBus.$emit('show-success', 'Data filter created.')
                this.mode = 'list'
                await this.fetchDataFilters()
            } catch (err) {
                EventBus.$emit('show-error', 'Failed to create data filter.')
            } finally {
                this.isSaving = false
            }
        },

        async onUpdate(filterId, draft) {
            this.isSaving = true

            try {
                const integrationId = this.getIntegrationIdNumber()
                if (!integrationId) {
                    EventBus.$emit('show-error', 'Missing integration id. Please refresh and try again.')
                    return
                }

                if (!filterId) {
                    EventBus.$emit('show-error', 'Missing filter id. Please refresh and try again.')
                    return
                }

                const payload = {
                    integration_id: integrationId,
                    ...this.normalizeDraftToPayload(draft),
                }

                await this.$http.post(`/api/data-filters/${filterId}`, payload)

                EventBus.$emit('show-success', 'Data filter updated.')
                this.mode = 'list'
                this.editFilterId = null
                this.editDraft = null
                await this.fetchDataFilters()
            } catch (err) {
                EventBus.$emit('show-error', 'Failed to update data filter.')
            } finally {
                this.isSaving = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
.v-dialog__container {
    display: unset;
}

.textarea-wrapper {
    display: flex;
    font-size: 14px;
    line-height: 1.5;
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
    background-color: #f4f4f4;
    height: auto;
}

.line-numbers {
    padding: 8px 12px;
    background-color: #f4f4f4;
    border-right: 1px solid #ccc;
    text-align: right;
    user-select: none;
    white-space: nowrap;
}

.custom-textarea ::v-deep(textarea) {
    font-size: 14px;
    line-height: 1.5;
    padding: 0;
    white-space: pre; /* No text wrapping */
    overflow-x: auto; /* Horizontal scroll */
    resize: none;
    min-height: auto;
    padding-bottom: 20px;
}

.wrap-cell {
    white-space: normal !important;
    word-break: break-word;
    display: block; /* ensures wrapping inside table cell */
}
</style>
